import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, filter, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { DialogsActions } from 'src/app/modules/dialogs/store/actions';
import { UserApiService } from 'src/app/modules/login/services/user-api.service';
import { PermissionsApiService } from 'src/app/modules/permissions/services/permissions-api.service';
import { AppConfigService } from 'src/app/shared/services/app.config.service';
import { ConfigurationApiService } from 'src/app/shared/services/configuration-api.service';
import { isPresent } from '../utils/isPresent';
import { AuthActions, ConfigurationActions, GetUserActions } from './actions';
import { selectClauseNameToBeFiltered, selectOnlyEmailAuthOption } from './selectors';
import { AppState } from './state';

@Injectable()
export class GetUserDetailsEffects {
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private userApiService: UserApiService,
    private permissionsApiService: PermissionsApiService,
    private translocoService: TranslocoService,
    private configurationApiService: ConfigurationApiService,
    private appConfigService: AppConfigService,
  ) {}

  getUserDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetUserActions.getUserDetails),
      mergeMap(() =>
        this.userApiService.getUserDetails$().pipe(
          map((res) => {
            // todo: del when task will be done
            // res.profileType = UserProfileType.Sales;
            return GetUserActions.getUserDetailsSuccess({ res });
          }),
          catchError(function (error) {
            return of(GetUserActions.getUserDetailsFailure({ error }));
          }),
        ),
      ),
    ),
  );

  saveUserDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetUserActions.saveUserDetails),
      mergeMap((props) =>
        this.userApiService.saveUserDetails$(props.user).pipe(
          mergeMap((user) =>
            of(
              GetUserActions.saveUserDetailsSuccess({ user }),
              GetUserActions.getUserDetails(),
            ),
          ),
          catchError((response) => {
            return of(
              GetUserActions.saveUserDetailsFailure(response),
              DialogsActions.showDialog({
                data: {
                  actionsKeys: ['close'],
                  content: '',
                  title: this.translocoService.translate('errors.saveUserDetails'),
                },
              }),
            );
          }),
        ),
      ),
    ),
  );

  getCurrentUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetUserActions.getCurrentUserPermissions),
      mergeMap(() =>
        this.permissionsApiService.getMyPermissions$().pipe(
          map((permissions) =>
            GetUserActions.getCurrentUserPermissionsSuccess({ permissions }),
          ),
          catchError((error) => of(GetUserActions.getCurrentUserPermissionsFailure({ error }))),
        ),
      ),
    ),
  );

  getUserDetailsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetUserActions.getUserDetailsFailure),
      withLatestFrom(this.store.select(selectOnlyEmailAuthOption)),
      mergeMap(([error, onlyEmail]) => {
        if (error.error.status === 404) {
          this.store.dispatch(GetUserActions.setInviting({ inviting: true }));
          if (!onlyEmail) {
            return this.userApiService.inviteUser$().pipe(
              map((res) => GetUserActions.inviteUserSuccess({ res })),
              catchError((error) => of(GetUserActions.inviteUserFailure({ error }))),
            );
          } else {
            return of(GetUserActions.inviteUserFailure(error));
          }
        } else {
          return of(GetUserActions.inviteUserFailure(error));
        }
      }),
    ),
  );

  setAuthAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setAuthAccount),
      debounceTime(500),
      withLatestFrom(this.store.select(selectClauseNameToBeFiltered).pipe(filter(isPresent))),
      map(([props, clauseName]) => {
        if (clauseName) {
          this.router.navigate([`clauses`]);
        }

        return AuthActions.clauseNameCheckedSuccess();
      }),
    ),
  );

  getConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationActions.getConfiguration),
      mergeMap(() =>
        this.configurationApiService.getConfiguration$().pipe(
          map((config) => {
            this.appConfigService.init(config);
            return ConfigurationActions.getConfigurationSuccess({ config });
          }),
          catchError((error) => of(ConfigurationActions.getConfigurationFailure({ error }))),
        ),
      ),
    ),
  );
}
